import { ChevronDown, ExternalLink, Lock, Moon, Search, Sun } from "lucide-react";
import { useCallback, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useDocs } from "../app/docs-context";
import { SearchDialog, useSearchShortcut } from "../features/search/search-dialog";
import type { NavCategory, NavItem } from "../shared/api/types";
import { useAsync } from "../shared/lib/use-async";
import { useTheme } from "../shared/lib/theme";

/**
 * The reader-facing frame: sticky header, collapsible sidebar, and the page.
 *
 * The navigation tree is fetched once here rather than per page — it is the same
 * for every route, and refetching it on each article would make navigation flash.
 */
export function DocsShell() {
  const [searchOpen, setSearchOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const { api, info, paths } = useDocs();
  const nav = useAsync((signal) => api.nav(signal), [api]);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  useSearchShortcut(openSearch);

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-chrome flex h-15 items-center gap-4 border-b px-6 backdrop-blur"
        style={{
          height: 60,
          borderColor: "var(--border)",
          background: "color-mix(in oklch, var(--background) 88%, transparent)",
        }}
      >
        <Link to={paths.home} className="flex items-center gap-2 no-underline">
          <span
            className="text-base font-bold tracking-tight"
            style={{ color: "var(--primary)", fontFamily: "var(--font-brand, inherit)" }}
          >
            {info.name}
          </span>
          <span
            className="rounded-full border px-2 py-0.5 text-xs font-medium"
            style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
          >
            Docs
          </span>
        </Link>

        <div className="flex-1" />

        <button
          type="button"
          onClick={openSearch}
          className="flex h-9 min-w-[230px] cursor-pointer items-center gap-2 rounded-md border px-3 text-sm"
          style={{
            background: "var(--muted)",
            borderColor: "var(--border)",
            color: "var(--muted-foreground)",
          }}
        >
          <Search className="size-4" aria-hidden />
          <span className="flex-1 text-left">Search documentation…</span>
          <kbd
            className="rounded border px-1.5 py-0.5 text-[0.7rem]"
            style={{
              fontFamily: "var(--font-mono)",
              background: "var(--background)",
              borderColor: "var(--border)",
            }}
          >
            ⌘K
          </kbd>
        </button>

        <button
          type="button"
          onClick={toggle}
          className="flex size-9 cursor-pointer items-center justify-center rounded-md border"
          style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
          aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
        >
          {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </button>

        {info.appUrl && (
          <a
            href={info.appUrl}
            className="flex size-9 items-center justify-center rounded-md border"
            style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
            title={`Open ${info.name}`}
            aria-label={`Open ${info.name}`}
          >
            <ExternalLink className="size-4" aria-hidden />
          </a>
        )}
      </header>

      <div className="mx-auto flex max-w-[1440px]">
        <aside
          className="sticky hidden shrink-0 overflow-y-auto border-r py-6 pl-6 pr-3 lg:block"
          style={{
            top: 60,
            width: 262,
            height: "calc(100vh - 60px)",
            borderColor: "var(--border)",
          }}
        >
          {nav.data?.map((category) => (
            <SidebarCategory key={category.slug} category={category} />
          ))}
        </aside>

        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>

      {searchOpen && <SearchDialog open onClose={() => setSearchOpen(false)} />}
    </div>
  );
}

function SidebarCategory({ category }: { category: NavCategory }) {
  const location = useLocation();
  const { paths } = useDocs();

  // Open the section the reader is currently in, so deep-linking to an article
  // does not land them next to a collapsed tree.
  const containsCurrent = category.sections.some((section) =>
    section.articles.some(
      (item) =>
        location.pathname === paths.article(item.slug) ||
        item.children.some((child) => location.pathname === paths.article(child.slug)),
    ),
  );

  const [expanded, setExpanded] = useState(containsCurrent || category.sections.length <= 2);

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full cursor-pointer items-center justify-between rounded-md px-2 py-1.5 text-left text-[0.82rem] font-semibold"
        style={{ color: "var(--foreground)" }}
      >
        <span>{category.name}</span>
        <ChevronDown
          className="size-3.5 transition-transform"
          style={{ transform: expanded ? undefined : "rotate(-90deg)" }}
          aria-hidden
        />
      </button>

      {expanded && (
        <div
          className="ml-2 mt-0.5 mb-2 flex flex-col gap-0.5 border-l pl-3"
          style={{ borderColor: "var(--border)" }}
        >
          {category.sections.map((section) => (
            <div key={section.name} className="mb-1">
              {/* Sections are labels rather than links: they have no page of
                  their own, only the articles beneath them. */}
              <div
                className="px-2 py-1 text-[0.72rem] font-semibold uppercase tracking-wide"
                style={{ color: "var(--muted-foreground)" }}
              >
                {section.name}
              </div>
              {section.articles.map((item) => (
                <SidebarItem key={item.slug} item={item} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarItem({ item, depth = 0 }: { item: NavItem; depth?: number }) {
  const { paths } = useDocs();
  return (
    <>
      <NavLink
        to={paths.article(item.slug)}
        className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm no-underline"
        style={({ isActive }) => ({
          color: isActive ? "var(--primary)" : "var(--muted-foreground)",
          fontWeight: isActive ? 600 : 400,
          background: isActive ? "var(--secondary)" : "transparent",
          paddingLeft: depth > 0 ? "1.5rem" : undefined,
        })}
      >
        <span className="flex-1">{item.title}</span>
        {/* Listed but locked: the reader is told the document exists so they know
            signing in would reveal it. */}
        {item.restricted && (
          <Lock className="size-3 shrink-0 opacity-60" aria-label="Sign-in required" />
        )}
      </NavLink>
      {item.children.map((child) => (
        <SidebarItem key={child.slug} item={child} depth={depth + 1} />
      ))}
    </>
  );
}
